import { List, CheckCircle, Clock, Filter, BookOpen, Layers } from "lucide-react";

export type GainStatusFilter = "all" | "unresolved" | "resolved";

interface LessonOption {
  id: string;
  title: string;
}

interface TopicOption {
  id: string;
  title: string;
}

interface DeficiencyFilterProps {
  selectedStatus: GainStatusFilter;
  onStatusChange: (status: GainStatusFilter) => void;
  selectedLesson: string;
  onLessonChange: (lessonId: string) => void;
  lessons: LessonOption[];
  selectedTopic: string;
  onTopicChange: (topicId: string) => void;
  topics: TopicOption[];
  topicsLoading?: boolean;
  onClearFilters: () => void;
}

export default function DeficiencyFilter({
  selectedStatus,
  onStatusChange,
  selectedLesson,
  onLessonChange,
  lessons,
  selectedTopic,
  onTopicChange,
  topics,
  topicsLoading,
  onClearFilters,
}: DeficiencyFilterProps) {
  const statusOptions = [
    {
      key: "all" as GainStatusFilter,
      label: "Tüm Eksikler",
      icon: List,
    },
    {
      key: "unresolved" as GainStatusFilter,
      label: "Bekleyen Eksikler",
      icon: Clock,
    },
    {
      key: "resolved" as GainStatusFilter,
      label: "Tamamlanan Eksikler",
      icon: CheckCircle,
    },
  ];

  const hasActiveFilters = () => {
    return selectedStatus !== "unresolved" || selectedLesson !== "" || selectedTopic !== "";
  };

  return (
    <div className="pr-2 w-full sm:w-64 h-full overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-800">Filtreler</span>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters() && (
            <button onClick={onClearFilters} className="text-xs text-red-600 hover:text-red-700 font-medium" title="Filtreleri temizle">
              Temizle
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4 sm:space-y-5">
        {/* Durum Filtresi */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">Eksik Durumu</label>
          <div className="space-y-1">
            {statusOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedStatus === option.key;

              return (
                <button
                  key={option.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(option.key);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                    isSelected ? "bg-blue-50 text-blue-700 border border-blue-200 font-medium" : "text-gray-700 hover:bg-gray-100"
                  }`}>
                  <Icon className={`w-4 h-4 ${isSelected ? "text-blue-600" : "text-gray-500"}`} />
                  <span className="flex-1">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Ders Filtresi */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            <div className="flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" />
              Ders
            </div>
          </label>
          <select
            value={selectedLesson}
            onChange={(e) => onLessonChange(e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium">
            <option value="">Tüm Dersler</option>
            {lessons.map((lesson) => (
              <option key={lesson.id} value={lesson.id}>
                {lesson.title}
              </option>
            ))}
          </select>
        </div>

        {/* Konu Filtresi */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            <div className="flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" />
              Bölüm
            </div>
          </label>
          <select
            value={selectedTopic}
            onChange={(e) => onTopicChange(e.target.value)}
            disabled={!selectedLesson || topicsLoading}
            className={`w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium ${
              !selectedLesson ? "opacity-50 cursor-not-allowed" : ""
            }`}>
            <option value="">{!selectedLesson ? "Ders Seçin" : topicsLoading ? "Yükleniyor..." : "Tüm Konular"}</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.title}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
